import React, { useEffect, useRef, useState } from 'react';
import { Text, View, StyleSheet } from 'react-native';
import { i18nService } from '../../../../services/i18n.service';
import httpService from '../../../../services/http.service';
import { fetchEmployeesFilterList } from '../../../../store/actions/reportsFilters.actions';
import CheckboxTable from './CheckboxTable';
import I18n from '../../../../components/shared/i18n/I18n.component';

// Parses a location string in the format "<address> - (<name>)".
// e.g. "רחוב הרצל 5 - (סניף מרכז)" → { address: "רחוב הרצל 5", name: "סניף מרכז" }
// If the string doesn't match the pattern, returns the raw string as address and an empty name.
const parseLocationName = (raw: string): { address: string; name: string } => {
	const match = raw?.match(/^(.*?)\s*-\s*\(([^)]+)\)\s*$/);
	return match
		? { address: match[1].trim(), name: match[2].trim() }
		: { address: raw ?? '', name: '' };
};

export const searchLocation = async (search: string, unitId: number) => {
	try {
		const res = await httpService.api<any>({
			type: 'getLocationFilter',
			disableBI: true,
			query: {
				pageName: 'locationSettings',
				searchText: search,
				unitIds: unitId,
			},
		});
		return res;
	} catch (e) {}
};

const locationColumns = [
	{
		accessor: 'locationName',
		Header: i18nService.translate('locationSettings.table.address'),
		isSortable: false,
		CustomCell: ({ row }: any) => (
			<I18n size={14} color='clockRecordBlue'>{parseLocationName(row.original.name).address}</I18n>
		),
	},
	{
		accessor: 'name',
		Header: i18nService.translate('locationSettings.table.addressName'),
		isSortable: false,
		CustomCell: ({ row }: any) => (
			<I18n size={14} color='clockRecordBlue'>{parseLocationName(row.original.name).name}</I18n>
		),
	},
];

const employeeColumns = [
	{
		accessor: 'name',
		Header: i18nService.translate(
			'modal.addEditDetails.locationDetails.employeeName',
		),
		isSortable: false,
	},
	{
		accessor: 'teudatZeut',
		Header: i18nService.translate('documents.table.header.teudatZeut'),
		isSortable: false,
	},
];

const projectColumns = [
	{
		accessor: 'name',
		Header: i18nService.translate('locationSettings.table.projects'),
		isSortable: false,
	},
];

const PAGE_SIZE = 20;

export const EmployeeSelector = ({
	locations,
	userIds,
	locationIds,
	setLocationIds,
	setUserIds,
	unitId,
	tableHeight,
}: {
	userIds: number[];
	locationIds: string[];
	locations: any[];
	setLocationIds: any;
	setUserIds: any;
	unitId: number;
	tableHeight?: number;
}) => {
	const [locationsList, setLocationsList] = useState(locations);
	const [employees, setEmployees] = useState<any[]>([]);
	const [allDataFetched, setAllDataFetched] = useState(false);
	const pageRef = useRef(0);
	const searchRef = useRef('');
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);


	useEffect(() => {
		setLocationsList(locations);
	}, [locations]);

	const loadEmployees = async (page: number, search: string, reset: boolean) => {
		try {
			const res = await fetchEmployeesFilterList('locationSettings', {
				page,
				pageSize: PAGE_SIZE,
				search,
				filter: { dep: [], internal: ['true'] },
				unitIds: [unitId],
			});
			const newData = res?.data || [];
			setEmployees(prev => (reset ? newData : [...prev, ...newData]));
			setAllDataFetched(newData.length < PAGE_SIZE);
			pageRef.current = page + 1;
		} catch (e) {}
	};

	useEffect(() => {
		if (unitId) {
			pageRef.current = 0;
			searchRef.current = '';
			loadEmployees(0, '', true);
		}
	}, [unitId]);

	const onLocationSearch = (query: string) => {
		if (debounceRef.current){
			clearTimeout(debounceRef.current);
		} 
			
		debounceRef.current = setTimeout(async () => {
			const res = await searchLocation(query, unitId);
			setLocationsList(res || []);
		}, 300);
	};

	const onEmployeeSearch = async (query: string) => {
		searchRef.current = query;
		pageRef.current = 0;
		await loadEmployees(0, query, true);
	};

	const getNextEmployees = () => {
		if (!allDataFetched) {
			loadEmployees(pageRef.current, searchRef.current, false);
		}
	};

	return (
		<View style={styles.dataContainer}>
			<CheckboxTable
				data={locationsList}
				columns={locationColumns}
				keyName='id'
				selectedIds={locationIds}
				onSelectionChange={setLocationIds}
				searchPlaceholder={i18nService.translate(
					'filter.multiselect.searchLocation',
				)}
				onSearch={onLocationSearch}
				height={tableHeight}
			/>
			<CheckboxTable
				data={employees}
				columns={employeeColumns}
				keyName='employeeId'
				selectedIds={userIds}
				onSelectionChange={setUserIds}
				searchPlaceholder={i18nService.translate(
					'filter.multiselect.employees',
				)}
				onSearch={onEmployeeSearch}
				getNextData={getNextEmployees}
				allDataFetched={allDataFetched}
				height={tableHeight}
			/>
		</View>
	);
};

export const ProjectSelector = ({
	projects,
	locations,
	locationIds,
	setProjectLocationIds,
	setProjectIds,
	projectIds,
	unitId,
	tableHeight,
}: {
	locationIds: string[];
	projectIds: string[];
	projects: any[];
	locations: any[];
	setProjectLocationIds: any;
	setProjectIds: any;
	unitId: number;
	tableHeight?: number;
}) => {
	const [locationsList, setLocationsList] = useState(locations);
	const [projectsList, setProjectsList] = useState(projects);

	useEffect(() => {
		setLocationsList(locations);
	}, [locations]);

	useEffect(() => {
		setProjectsList(projects);
	}, [projects]);

	const onLocationSearch = async (query: string) => {
		const res = await searchLocation(query, unitId);
		setLocationsList(res || []);
	};

	const onProjectSearch = (query: string) => {
		if (!query) {
			setProjectsList(projects);
		} else {
			const q = query.toLowerCase();
			setProjectsList(projects.filter(p => p.name?.toLowerCase().includes(q)));
		}
	};

	return (
		<View style={styles.dataContainer}>
			<CheckboxTable
				data={locationsList}
				columns={locationColumns}
				keyName='id'
				selectedIds={locationIds}
				onSelectionChange={setProjectLocationIds}
				searchPlaceholder={i18nService.translate(
					'filter.multiselect.searchLocation',
				)}
				onSearch={onLocationSearch}
				height={tableHeight}
			/>
			<CheckboxTable
				data={projectsList}
				columns={projectColumns}
				keyName='id'
				selectedIds={projectIds}
				onSelectionChange={setProjectIds}
				searchPlaceholder={i18nService.translate(
					'filter.multiselect.searchProject',
				)}
				onSearch={onProjectSearch}
				height={tableHeight}
			/>
		</View>
	);
};

const styles = StyleSheet.create({
	dataContainer: {
		zIndex: 'unset' as any,
		width: '100%',
		flexDirection: 'row-reverse',
		justifyContent: 'space-between',
		gap: 16 as any,
	},
});
